import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, CheckCircle2, Building2, Loader2 } from 'lucide-react';
import { brand } from '../mock';
import SectionLabel from '../components/SectionLabel';
import { submitInquiry } from '../lib/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', org: '', email: '', scope: 'Contractor KPI Auditing', message: '' });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await submitInquiry(form);
      setSent(true);
      setForm({ name: '', org: '', email: '', scope: 'Contractor KPI Auditing', message: '' });
      setTimeout(() => setSent(false), 5200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  const scopes = [
    'Contractor KPI Auditing',
    'Road Asset Management',
    'TGS Review & Compliance',
    'MASH Barrier Assessment',
    'Stakeholder Engagement',
    'Data Analysis & Reporting',
  ];

  return (
    <div>
      <section className="pt-20 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-[1320px] mx-auto px-6">
          <SectionLabel icon={Mail}>CONTACT / ENGAGEMENT INTAKE</SectionLabel>
          <h1 className="mt-4 font-serif text-white text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Discuss an infrastructure advisory engagement.
          </h1>
          <p className="mt-5 max-w-2xl text-slate-400 text-lg leading-relaxed">
            Share your scope, timeline, and jurisdiction. Responses to advisory inquiries are typically returned within two business days.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1320px] mx-auto px-6 grid lg:grid-cols-5 gap-8">
          <motion.form
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            onSubmit={onSubmit}
            className="lg:col-span-3 rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-7"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">FULL NAME</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors"
                  placeholder="e.g. Elena Ricci" />
              </div>
              <div>
                <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">ORGANISATION</label>
                <input required value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })}
                  className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors"
                  placeholder="State agency / consultancy" />
              </div>
              <div>
                <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">EMAIL</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors"
                  placeholder="you@agency.gov.au" />
              </div>
              <div>
                <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">SCOPE OF INTEREST</label>
                <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white outline-none transition-colors">
                  {scopes.map((s) => <option key={s} value={s} className="bg-[#0a1120]">{s}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">ENGAGEMENT BRIEF</label>
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none transition-colors resize-none"
                placeholder="Outline the network, jurisdiction, timeline, and any contract framework in play." />
            </div>
            {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
            <div className="mt-6 flex items-center justify-between">
              <div className="text-xs text-slate-500">Your details are used only to respond to this inquiry.</div>
              <button type="submit" disabled={submitting}
                className="group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  : sent ? <><CheckCircle2 className="w-4 h-4" /> Inquiry Received</>
                  : <>Send Inquiry <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>}
              </button>
            </div>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 space-y-5"
          >
            <div className="rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-6">
              <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300 mb-4">DIRECT CONTACT</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-orange-400" /><span className="text-slate-200">{brand.email}</span></div>
                <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-orange-400" /><span className="text-slate-200">{brand.location}</span></div>
                <div className="flex items-center gap-3"><Building2 className="w-4 h-4 text-orange-400" /><span className="text-slate-200">{brand.scope}</span></div>
              </div>
            </div>
            <div className="rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-6">
              <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300 mb-4">TYPICAL RESPONSE</div>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Advisory brief acknowledged within 24 hours</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Scoping discussion arranged within 2 business days</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /> Written scope &amp; fee proposal within 5 business days</li>
              </ul>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
